/* ============================================
   BASE STYLES - Shared by all variants
   Visual design matched to TopCommandInput.
   ============================================ */

/* Container base styles */
.outerGroup {
    background-color: white;
    cursor: text;
    flex-wrap: wrap;
    align-items: center;
    box-shadow: none;
    border: 1px solid rgba(15, 15, 20, 0.2);
    border-radius: 0;
    padding: var(--mantine-spacing-sm) var(--mantine-spacing-sm);

    @container (min-width: $mantine-breakpoint-sm) {
        padding: var(--mantine-spacing-md) var(--mantine-spacing-md);
    }

    transition:
        padding 0.25s ease,
        opacity var(--transition-duration-fast) var(--transition-ease-snappy),
        border var(--transition-duration-fast) var(--transition-ease-snappy),
        transform var(--transition-duration-fast) var(--transition-ease-snappy);
}

/* Container wrap state */
.outerGroupWrap {
    flex-direction: column;
    align-items: stretch;
}

/* Container hover/focus/active states */
.outerGroup:hover {
    border: 1px solid var(--dib-ink);
}

.outerGroup:focus-within,
.outerGroup:active {
    border: 1px solid var(--mantine-color-dibblue-5);
    box-shadow: inset 0 0 0 1px var(--mantine-color-dibblue-5);
    background-color: white;
}

/* Textarea wrapper */
.textareaWrapper {
    flex: 1;
}

/* Textarea input base */
.input {
    width: 100%;
    will-change: height;
    flex: 1;
    border: none;
    background-color: transparent;
    min-height: 0;
    color: var(--dib-ink);
}

/* Remove scrollbar from textarea */
.input::-webkit-scrollbar {
    display: none;
}

/* Textarea placeholder */
.input::placeholder {
    color: rgba(15, 15, 20, 0.5);
}

.input:focus::placeholder {
    color: transparent;
}

/* Hidden text for width calculation */
.hiddenText {
    position: absolute;
    visibility: hidden;
    white-space: pre-wrap;
    font-family: Lato;
}

/* Button group */
.buttonGroup {
    flex-wrap: nowrap;
    gap: var(--mantine-spacing-xs);
}

/* Send button base */
.sendButton {
    background-color: var(--dib-brand-base);
    color: white;
    border-radius: var(--mantine-radius-xl);
    transition: background-color 0.1s ease-in-out;
    /* Even dimensions keep the even-sized icon on whole pixels (no 0.5px
       rounding drift on 1x displays). */
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
    padding: 0;

    @container (min-width: $mantine-breakpoint-sm) {
        width: 32px;
        height: 32px;
        min-width: 32px;
        min-height: 32px;
    }
}

.sendButton:hover {
    background-color: var(--dib-brand-base);
    color: white;
    opacity: 0.85;
}

.sendButton:disabled,
.sendButton[data-disabled] {
    background-color: var(--mantine-color-gneis-1);
    color: var(--mantine-color-gneis-6);
    opacity: 1;
    cursor: not-allowed;
}

.sendButton:disabled:hover,
.sendButton[data-disabled]:hover {
    background-color: var(--mantine-color-gneis-2);
    color: var(--mantine-color-gneis-6);
    opacity: 1;
}

.sendButtonInner {
    position: relative;
    width: 100%;
    height: 100%;
}

/* ============================================
   FUNCTIONAL STATE STYLES (CommandInput-only)
   ============================================ */

/* At-capacity state: visually disabled but clickable for feedback */
.sendButtonAtCapacity,
.sendButtonAtCapacity:hover,
.sendButtonAtCapacity:active {
    opacity: 0.55;
    cursor: not-allowed;
    background-color: var(--dib-brand-base);
    color: white;
}

/* Shake animation triggered on blocked submit */
@keyframes sendButtonShake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-1.5px); }
    50% { transform: translateX(1.5px); }
    75% { transform: translateX(-1px); }
}

.sendButtonShake {
    animation: sendButtonShake 0.32s ease-in-out;
}

/* Icon base styles */
.icon {
    display: block;
    width: 16px;
    height: 16px;
}

/* Icon visibility control - all hidden by default */
.iconArrowRight {
    display: none;
}

.iconArrowUp {
    display: none;
}

/* Backslash overlay — sits on top of the arrow when at capacity */
.iconBackslash {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
}

/* ============================================
   SPLASH VARIANT STYLES
   ============================================ */

/* Splash container - no additional styles needed, uses base */
.outerGroup--splash {
    /* Inherits all base styles */
}

/* Splash input - larger font size */
.input--splash {
    font-size: var(--mantine-font-size-sm);
    padding-left: 1px;

    @container (min-width: $mantine-breakpoint-sm) {
        font-size: var(--mantine-font-size-lg);
        line-height: 1.4;
        padding: 0;
    }
}

/* Splash hidden text - matches input font size */
.outerGroup--splash .hiddenText {
    font-size: var(--mantine-font-size-sm);

    @container (min-width: $mantine-breakpoint-sm) {
        font-size: var(--mantine-font-size-lg);
    }
}

/* Splash icon - show arrow-right */
.outerGroup--splash .iconArrowRight {
    display: block;
}

/* ============================================
   CHAT VARIANT STYLES
   ============================================ */

/* Chat container - no additional styles needed, uses base */
.outerGroup--chat {
    /* Inherits all base styles */
}

/* Chat input - standard font size */
.input--chat {
    font-size: var(--mantine-font-size-sm);
    padding-left: 1px;

    @container (min-width: $mantine-breakpoint-sm) {
        font-size: var(--mantine-font-size-lg);
        line-height: 1.4;
        padding: 0;
    }
}

/* Chat hidden text - matches input font size */
.outerGroup--chat .hiddenText {
    font-size: var(--mantine-font-size-sm);

    @container (min-width: $mantine-breakpoint-sm) {
        font-size: var(--mantine-font-size-lg);
    }
}

/* Chat icon - show arrow-up */
.outerGroup--chat .iconArrowUp {
    display: block;
}

/* ============================================
   TRIGGER SEARCH ACTIVE STATE
   ============================================ */

.triggerSearchActive {
    /* Reserved — word-level highlights handle the visual signal */
}
